import { Callback1, SingleIndex } from '@deqo/foundation';
import { differenceInDays, differenceInWeeks, startOfDay, startOfWeek } from 'date-fns';
import React from 'react';
import styled from 'styled-components';
import { LabelValue } from '../../platform/types/LabelValue';

const bucketDefs = {
    today: '% Today',
    yesterday: '% Yesterday',
    thisWeek: '% This Week',
    lastWeek: '% Last Week',
    whileAgo: '% a while ago...',
    never: 'Never %',
};

const selectBucket = (item: any, prop: LabelValue): keyof typeof bucketDefs => {

    const date = item[prop.value];

    if (date instanceof Date) {
        const dd = differenceInDays(
            startOfDay(new Date()),
            startOfDay(date)
        );

        if (dd === 0) return 'today';
        if (dd === 1) return 'yesterday';

        const dw = differenceInWeeks(
            startOfWeek(new Date()),
            startOfWeek(date)
        );

        if (dw === 0) return 'thisWeek';
        if (dw === 1) return 'lastWeek';

        return 'whileAgo';
    }
    else {
        return 'never';
    }
};

const sortItems = (items: any[], prop: LabelValue) =>
    items.sort((a, b) => {
        const ap = a[prop.value];
        if (!(ap instanceof Date)) return -1;
        const bp = b[prop.value];
        if (!(bp instanceof Date)) return 1;
        return bp.getTime() - ap.getTime();
    });

const reverseIfAsc = <T extends unknown>(items: T[], order: 'asc' | 'desc') =>
    order === 'asc' ? items.slice().reverse() : items;

const toBuckets = (items: any[], prop: LabelValue, order: 'asc' | 'desc') => {
    const index = new SingleIndex<string, any>();
    items.forEach(x => index.add(selectBucket(x, prop), x));
    return reverseIfAsc(Object.keys(bucketDefs), order).map(x => {
        const label = bucketDefs[x].replace('%', prop.label);
        const contents = reverseIfAsc(sortItems(Array.from(index.get(x)), prop), order);
        return [label, contents] as const;
    });
};

export type Bucket<T> = {
    label: string,
    items: T[],
};

export const useDateBuckets = <T extends unknown>(items: T[], using: LabelValue, order: 'asc' | 'desc') => {
    // eslint-disable-next-line no-shadow
    return toBuckets(items, using, order).map(([label, items]) => ({ label, items }));
};

export interface DateBucketProps<T> {
    items: T[];
    using: LabelValue;
    order?: 'asc' | 'desc';
    render: Callback1<JSX.Element, { label: string, items: T[] }>;
}

const BucketFrame = styled.div``;

const Base = React.memo(
    function <T>(props: DateBucketProps<T>) {
        const buckets = toBuckets(props.items, props.using as any, props.order);
        return <>
            {buckets.map(([label, items]) =>
                <BucketFrame key={label ?? ''}>
                    {props.render({ label, items })}
                </BucketFrame>
            )}
        </>;
    }
);

export const DateBucketView = Object.assign(Base, { BucketFrame });
